import React from 'react';
import { FormApi } from 'final-form';
import { AnyObject } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { ClickAwayListener, Paper, IconButton, Typography, AccordionSummary, Button, AccordionDetails, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import moment from 'moment';
import { ContactEnquiry, ContactMatterList, DuplicateCheck } from '../../../hooks/useCrmDuplicateCheck';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from '../panels/arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('DuplicateContactInfoPopup');

const usePopupStyles = makeStyles((theme) =>
    createStyles({
        paper: {
            position: 'relative',
            padding: 0,
            borderRadius: 12,
            overflow: 'hidden',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 56px 16px 20px',
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        headerIcon: {
            flex: '0 0 auto',
            width: 40,
            height: 40,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 152, 0, 0.14)',
            color: '#ed6c02',
            '& svg': {
                fontSize: 22,
            },
        },
        headerTitle: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        headerSubtitle: {
            color: theme.palette.text.secondary,
            fontSize: '0.8125rem',
            lineHeight: 1.3,
        },
        closeButton: {
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 1,
        },
        // The sections stack with one even gap. The wrappers around a section that has no rows render empty (they only add spacing), so they are hidden, and the
        // old per-section top padding and margin give way to the gap.
        body: {
            '&&': {
                gap: 12,
                padding: 16,
            },
            '& > div:empty': {
                display: 'none',
            },
            // The list is a column with a maximum height, so when there are many matches its items would shrink to fit it - and a card with overflow:hidden can
            // shrink all the way down to its border. They keep their height and the list scrolls instead.
            '& > div': {
                paddingTop: '0 !important',
                flexShrink: 0,
            },
        },
        // each match is a flat, rounded card instead of a raised accordion
        card: {
            flexShrink: 0,
            '&&': {
                margin: '0 !important',
            },
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px !important',
            overflow: 'hidden',
            '&:before': {
                display: 'none',
            },
            '& .MuiAccordionSummary-root': {
                backgroundColor: '#f8f9fc',
            },
            '& .MuiAccordionDetails-root': {
                padding: '12px 16px 16px',
            },
            '& .MuiAccordionSummary-content': {
                margin: '10px 0',
                alignItems: 'center',
            },
        },
        contactChip: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            borderRadius: 999,
            backgroundColor: '#f2f4fa',
            color: theme.palette.text.primary,
            fontSize: '0.8125rem',
            textDecoration: 'none',
            '&:hover': {
                backgroundColor: '#e6e9f5',
            },
            '& svg': {
                fontSize: 16,
                color: '#667085',
            },
        },
        table: {
            '& .MuiTableCell-head': {
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiTableCell-body': {
                fontSize: '0.875rem',
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                borderBottom: 'none',
            },
            '& .MuiTableBody-root .MuiTableRow-root:hover': {
                backgroundColor: 'rgba(22, 103, 199, 0.04)',
            },
            '& a': {
                color: '#1667c7',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                    textDecoration: 'underline',
                },
            },
        },
    }),
);

// Explicit request (EnquiryFormDrawer split): the "Duplicate Names" popup content, moved out of the drawer's
// showAllContactInfo render function with its JSX unchanged. It is memoised and gets only stable props, so
// it no longer re-renders every time the drawer does. The form values are read when "Select" is clicked
// (form.getState()) - the same values the render-time closure held, since the popup used to re-render on
// every change.
export interface DuplicateContactInfoPopupProps {
    form: FormApi<Enquiry>;
    duplicateCheck: DuplicateCheck | null;
    classes: EnquiryFormClasses;
    history: ReturnType<typeof useHistory>;
    onClose: () => void;
    onUseThisContact: (form: FormApi<Enquiry>, values: AnyObject, matter: ContactMatterList, emaillAddress: string) => () => void;
    onEnquiryRowClick: (row: ContactEnquiry) => () => void;
}

export const DuplicateContactInfoPopup: React.FC<DuplicateContactInfoPopupProps> = React.memo((popupProps) => {
    const { form, duplicateCheck, classes, history, onClose, onUseThisContact, onEnquiryRowClick } = popupProps;
    const popupClasses = usePopupStyles();

    if (!duplicateCheck) {
        return (
            <div />
        );
    }

    return (
        <ClickAwayListener onClickAway={() => {
            // Don't close on text selection
            if (window.getSelection()?.toString()) return;
            onClose()
        }}>
            <Paper
                elevation={3}
                className={`user-select-text ${popupClasses.paper}`}
            >
                <IconButton
                    className={popupClasses.closeButton}
                    size="small"
                    aria-label="Close"
                >
                    <CloseIcon onClick={() => onClose()} />
                </IconButton>
                <div className={popupClasses.header}>
                    <span className={popupClasses.headerIcon}>
                        <PeopleOutlineIcon />
                    </span>
                    <div>
                        <Typography className={popupClasses.headerTitle}>
                            Duplicate Names
                        </Typography>
                        <Typography className={popupClasses.headerSubtitle}>
                            Contacts and enquiries that already use this name or email
                        </Typography>
                    </div>
                </div>
                <div className={`${classes.showContactWrapper} ${popupClasses.body}`}>
                    {duplicateCheck.partyDuplicateCount > 0 && (
                        <>
                            {/* {duplicateCheck.partyDuplicateCount > 1 ? (
                                <Typography style={{ fontStyle: 'italic' }}>Multiple contacts found, first below</Typography>
                            ) : null} */}
                            {/* {duplicateCheck.contactName ? (
                                <div>
                                    <Typography>Contact Name: {duplicateCheck.contactName}</Typography>
                                </div>
                            ) : null} */}
                            {/* {duplicateCheck.contactEmailAddress ? (
                                <div>
                                    <Typography>Contact Email: {duplicateCheck.contactEmailAddress}</Typography>
                                </div>
                            ) : null} */}
                            {duplicateCheck.contactMatterList && duplicateCheck.contactMatterList.map((matter) => (
                                <Accordion
                                    key={matter.nameId}
                                    defaultExpanded={true}
                                    classes={{
                                        root: `${classes.accordionRoot} ${popupClasses.card}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`duplicate-contact-${matter.nameId}-content`}
                                        id={`duplicate-contact-${matter.nameId}-header`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <Typography className={classes.heading}>
                                                {matter.name}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                onClick={() => onUseThisContact(form, form.getState().values, matter, duplicateCheck.contactEmailAddress)()}
                                                size="small"
                                            >
                                                Select
                                            </Button>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <div>
                                            <div
                                                style={{
                                                    paddingBottom: '10px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                    
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        // flexDirection: 'column',
                                                        gap: '5px',
                                                    }}
                                                >
                                                    {matter.phone && 
                                                        <Typography
                                                            component="a"
                                                            href={`tel:${matter.phone}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <PhoneOutlinedIcon /> {matter.phone}
                                                        </Typography>
                                                    }
                                                    {matter.mobile && 
                                                        <Typography
                                                            component="a"
                                                            href={`tel:${matter.mobile}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <PhoneIphoneIcon /> {matter.mobile}
                                                        </Typography>
                                                    }
                                                    {duplicateCheck.contactEmailAddress && 
                                                        <Typography
                                                            component="a"
                                                            href={`mailto:${duplicateCheck.contactEmailAddress}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <MailOutlineIcon /> {duplicateCheck.contactEmailAddress}
                                                        </Typography>
                                                    }
                                                </div>
                                            </div>
                                            {matter.contactMatters && matter.contactMatters.length > 0 && (
                                                <TableContainer>
                                                    <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>File No</TableCell>
                                                                <TableCell>Matter Type</TableCell>
                                                                <TableCell>Role</TableCell>
                                                                <TableCell>Status</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {matter.contactMatters.map((row) => (
                                                                <TableRow key={row.id}>
                                                                    <TableCell component="th" scope="row">
                                                                        <a 
                                                                            href='#'
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                const href = history.createHref({
                                                                                    pathname: `/matterDetail/${row.id}`,
                                                                                    key: 'matterManagement',
                                                                                    state: { matterManagementState: 'false' }
                                                                                });
                                                                                window.open(href, '_blank');
                                                                            }}
                                                                        >
                                                                            {row.fileNumber}
                                                                        </a>
                                                                    </TableCell>
                                                                    <TableCell>{row.type}</TableCell>
                                                                    <TableCell>{row.roles}</TableCell>
                                                                    <TableCell>{row.status}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </>
                    )}
                        
                    {duplicateCheck.partyDuplicateCount > 0 && (
                        <>
                            {/* {duplicateCheck.contactName && duplicateCheck.contactMatterListByName && duplicateCheck.contactMatterListByName.length > 0 ? (
                                <div>
                                    <Typography>Contact Name: {duplicateCheck.contactName}</Typography>
                                </div>
                            ) : null} */}
                            {/* A contact that matches the name and the email is in both lists - it is shown once, in the email list above. */}
                            {duplicateCheck.contactMatterListByName && duplicateCheck.contactMatterListByName
                                .filter((matter) => !(duplicateCheck.contactMatterList || []).some((emailMatter) => emailMatter.nameId === matter.nameId))
                                .map((matter) => (
                                <Accordion
                                    key={matter.nameId}
                                    defaultExpanded={true}
                                    classes={{
                                        root: `${classes.accordionRoot} ${popupClasses.card}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`duplicate-contact-${matter.nameId}-content`}
                                        id={`duplicate-contact-${matter.nameId}-header`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <Typography className={classes.heading}>
                                                {matter.name}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                onClick={() => onUseThisContact(form, form.getState().values, matter, duplicateCheck.contactEmailAddress)()}
                                                size="small"
                                            >
                                                Select
                                            </Button>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <div>
                                            <div
                                                style={{
                                                    paddingBottom: '10px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                    
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        // flexDirection: 'column',
                                                        gap: '5px',
                                                    }}
                                                >
                                                    {matter.phone && 
                                                        <Typography
                                                            component="a"
                                                            href={`tel:${matter.phone}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <PhoneOutlinedIcon /> {matter.phone}
                                                        </Typography>
                                                    }
                                                    {matter.mobile && 
                                                        <Typography
                                                            component="a"
                                                            href={`tel:${matter.mobile}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <PhoneIphoneIcon /> {matter.mobile}
                                                        </Typography>
                                                    }
                                                    {duplicateCheck.contactEmailAddress && 
                                                        <Typography
                                                            component="a"
                                                            href={`mailto:${duplicateCheck.contactEmailAddress}`}
                                                            className={popupClasses.contactChip}
                                                        >
                                                            <MailOutlineIcon /> {duplicateCheck.contactEmailAddress}
                                                        </Typography>
                                                    }
                                                </div>
                                            </div>
                                            {matter.contactMatters && matter.contactMatters.length > 0 && (
                                                <TableContainer>
                                                    <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>File No</TableCell>
                                                                <TableCell>Matter Type</TableCell>
                                                                <TableCell>Role</TableCell>
                                                                <TableCell>Status</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {matter.contactMatters.map((row) => (
                                                                <TableRow key={row.id}>
                                                                    <TableCell component="th" scope="row">
                                                                        <a 
                                                                            href='#'
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                const href = history.createHref({
                                                                                    pathname: `/matterDetail/${row.id}`,
                                                                                    key: 'matterManagement',
                                                                                    state: { matterManagementState: 'false' }
                                                                                });
                                                                                window.open(href, '_blank');
                                                                            }}
                                                                        >
                                                                            {row.fileNumber}
                                                                        </a>
                                                                    </TableCell>
                                                                    <TableCell>{row.type}</TableCell>
                                                                    <TableCell>{row.roles}</TableCell>
                                                                    <TableCell>{row.status}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </>
                    )}
                        
                    {duplicateCheck.enquiryContactDuplicateCount > 0 && (
                        <div style={{ paddingTop: '10px' }}>
                            {/* {duplicateCheck.enquiryContactDuplicateCount > 1 ? (
                                <Typography style={{ fontStyle: 'italic' }}>Multiple enquiries found, first below</Typography>
                            ) : null} */}
                            {duplicateCheck.contactEnquiries && duplicateCheck.contactEnquiries.length > 0 && (
                                <Accordion
                                    defaultExpanded={true}
                                    classes={{
                                        root: `${classes.accordionRoot} ${popupClasses.card}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="duplicate-contact-section-1-content"
                                        id="duplicate-contact-section-1-header"
                                    >
                                       <div>
                                            {/* {duplicateCheck.enquiryName ? (
                                                <div>
                                                    <Typography>Enquiry Name: {duplicateCheck.enquiryName}</Typography>
                                                </div>
                                            ) : null} */}
                                            {duplicateCheck.enquiryEmailAddress ? (
                                                <div>
                                                    <Typography>Enquiry Email: {duplicateCheck.enquiryEmailAddress}</Typography>
                                                </div>
                                            ) : null}
                                       </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer>
                                            <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Enquiry Name</TableCell>
                                                        <TableCell>Area of Law</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Assignee</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {duplicateCheck.contactEnquiries.map((row, rowIndex) => (
                                                        <TableRow key={`${row.enquiryGuid || row.assignedToGuid}-${rowIndex}`}>
                                                            <TableCell component="th" scope="row">
                                                                <a
                                                                    href='#'
                                                                    onClick={onEnquiryRowClick(row)}
                                                                >
                                                                    {row.enquiryName}
                                                                </a>
                                                            </TableCell>
                                                            <TableCell>{row.areaOfLaw}</TableCell>
                                                            <TableCell>
                                                                {row.enquiryDate ? moment(row.enquiryDate).format('DD-MMM-YYYY') : ''}
                                                            </TableCell>
                                                            <TableCell>{row.assignedToName}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </div>
                    )}
                    {duplicateCheck.enquiryContactDuplicateCount > 0 && (
                        <div style={{ paddingTop: '10px' }}>
                            {/* {duplicateCheck.enquiryContactDuplicateCount > 1 ? (
                                <Typography style={{ fontStyle: 'italic' }}>Multiple enquiries found, first below</Typography>
                            ) : null} */}
                            {duplicateCheck.contactEnquiriesByName && duplicateCheck.contactEnquiriesByName.length > 0 && (
                                <Accordion
                                    defaultExpanded={true}
                                    classes={{
                                        root: `${classes.accordionRoot} ${popupClasses.card}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="duplicate-contact-section-2-content"
                                        id="duplicate-contact-section-2-header"
                                    >
                                       <div>
                                            {/* {duplicateCheck.enquiryName ? (
                                                <div>
                                                    <Typography>Enquiry Name: {duplicateCheck.enquiryName}</Typography>
                                                </div>
                                            ) : null} */}
                                            {duplicateCheck.enquiryName ? (
                                                <div>
                                                    <Typography>Enquiry Name: {duplicateCheck.enquiryName}</Typography>
                                                </div>
                                            ) : null}
                                       </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer>
                                            <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Enquiry Name</TableCell>
                                                        <TableCell>Area of Law</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Assignee</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {duplicateCheck.contactEnquiriesByName.map((row, rowIndex) => (
                                                        <TableRow key={`${row.enquiryGuid || row.assignedToGuid}-${rowIndex}`}>
                                                            <TableCell component="th" scope="row">
                                                                <a
                                                                    href='#'
                                                                    onClick={onEnquiryRowClick(row)}
                                                                >
                                                                    {row.enquiryName}
                                                                </a>
                                                            </TableCell>
                                                            <TableCell>{row.areaOfLaw}</TableCell>
                                                            <TableCell>
                                                                {row.enquiryDate ? moment(row.enquiryDate).format('DD-MMM-YYYY') : ''}
                                                            </TableCell>
                                                            <TableCell>{row.assignedToName}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </div>
                    )}
                    <div style={{ paddingTop: '10px' }}>
                        {duplicateCheck.contactEnquriesOtherSide && duplicateCheck.contactEnquriesOtherSide.length > 0 && (
                            <Accordion
                                defaultExpanded={true}
                                classes={{
                                    root: `${classes.accordionRoot} ${popupClasses.card}`,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="duplicate-contact-section-3-content"
                                    id="duplicate-contact-section-3-header"
                                >
                                    <div>
                                        {/* {duplicateCheck.otherSideName ? (
                                            <div>
                                                <Typography>Enquiry Other Side Name: {duplicateCheck.otherSideName}</Typography>
                                            </div>
                                        ) : null} */}
                                        {duplicateCheck.otherSideEmailAddress ? (
                                            <div>
                                                <Typography>Enquiry Other Side Email: {duplicateCheck.otherSideEmailAddress}</Typography>
                                            </div>
                                        ) : null}
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer>
                                        <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Enquiry Name</TableCell>
                                                    <TableCell>Area of Law</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Assignee</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {duplicateCheck.contactEnquriesOtherSide.map((row, rowIndex) => (
                                                    <TableRow key={`${row.enquiryGuid || row.assignedToGuid}-${rowIndex}`}>
                                                        <TableCell component="th" scope="row">
                                                            <a
                                                                href='#'
                                                                onClick={onEnquiryRowClick(row)}
                                                            >
                                                                {row.enquiryName}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell>{row.areaOfLaw}</TableCell>
                                                        <TableCell>
                                                            {row.enquiryDate ? moment(row.enquiryDate).format('DD-MMM-YYYY') : ''}
                                                        </TableCell>
                                                        <TableCell>{row.assignedToName}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </div>
                        
                    <div style={{ paddingTop: '10px' }}>
                        {duplicateCheck.contactEnquriesOtherSideByName && duplicateCheck.contactEnquriesOtherSideByName.length > 0 && (
                            <Accordion
                                defaultExpanded={true}
                                classes={{
                                    root: `${classes.accordionRoot} ${popupClasses.card}`,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="duplicate-contact-section-4-content"
                                    id="duplicate-contact-section-4-header"
                                >
                                    <div>
                                        {/* {duplicateCheck.otherSideName ? (
                                            <div>
                                                <Typography>Enquiry Other Side Name: {duplicateCheck.otherSideName}</Typography>
                                            </div>
                                        ) : null} */}
                                        {duplicateCheck.otherSideName ? (
                                            <div>
                                                <Typography>Enquiry Other Side Name: {duplicateCheck.otherSideName}</Typography>
                                            </div>
                                        ) : null}
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer>
                                        <Table size="small" aria-label="a dense table" className={popupClasses.table}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Enquiry Name</TableCell>
                                                    <TableCell>Area of Law</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Assignee</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {duplicateCheck.contactEnquriesOtherSideByName.map((row, rowIndex) => (
                                                    <TableRow key={`${row.enquiryGuid || row.assignedToGuid}-${rowIndex}`}>
                                                        <TableCell component="th" scope="row">
                                                            <a
                                                                href='#'
                                                                onClick={onEnquiryRowClick(row)}
                                                            >
                                                                {row.enquiryName}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell>{row.areaOfLaw}</TableCell>
                                                        <TableCell>
                                                            {row.enquiryDate ? moment(row.enquiryDate).format('DD-MMM-YYYY') : ''}
                                                        </TableCell>
                                                        <TableCell>{row.assignedToName}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </div>
                </div>
            </Paper>
        </ClickAwayListener>
    );
}, arePropsEqualIgnoringForm);
DuplicateContactInfoPopup.displayName = 'DuplicateContactInfoPopup';
